<script lang="ts" module>
  import { splitInTwo } from '#lib/overview/utils/string.js';
  import { toPrimitive, type primitive, type tagId, type thresholdId } from './FilterRow.utils';
  import TagFilter from './TagFilter.svelte';
  import ThresholdFilter from './ThresholdFilter.svelte';
  import type { TableRow } from '#lib/zod/output/TabularData.js';
  import { bottomUpTraverse, traverseTable } from '../data-table/utils';
  import { filter, flat, map, some } from '#lib/utils/generators.js';
  import { SvelteMap, SvelteSet } from 'svelte/reactivity';
  import { untrack } from 'svelte';
  import type { ClassValue } from 'svelte/elements';

  const identity = (x: any) => x;
</script>

<script lang="ts">
  interface Props {
    rows: TableRow[];
    useAnyTagFilter: boolean;
    tagLabel?: (tagTitle: string) => string;
    tagTitle?: (tagTitle: string) => string;
    /** @bind */
    filterFn: (r: TableRow) => boolean;
    class?: ClassValue;
  }

  let {
    rows,
    useAnyTagFilter,
    tagLabel = identity,
    tagTitle = identity,
    // eslint-disable-next-line no-useless-assignment
    filterFn = $bindable(),
    class: class_,
  }: Props = $props();

  const empty = new SvelteSet<never>();

  let thresholdFilters = new Map<thresholdId, number>([/*['threshold:absolute|>=', 10]*/]);

  const tagOptions = (id: tagId): Array<{ title: string; value: primitive; active: boolean }> => {
    const [type, key] = splitInTwo(id, ':');
    switch (type) {
      case 'parameter': {
        const possibilities = [
          ...new Set(
            map(
              filter(traverseTable(rows), (r) => r.parameters[key] !== undefined),
              (r) => toPrimitive(r.parameters[key]),
            ),
          ),
        ];
        return possibilities.map((p) => ({
          title: String(p),
          value: p,
          active: (tagFilters.get(id) ?? empty).has(p),
        }));
      }
      case 'tag': {
        if (key === 'any') {
          const possibilities = [...new Set(flat(map(traverseTable(rows), (r) => r.tags)))];
          return possibilities.map((p) => ({
            title: String(p),
            value: p,
            active: (tagFilters.get(id) ?? empty).has(p),
          }));
        } else
          return [true, false].map((p) => ({
            title: p ? 'Yes' : 'No',
            value: p,
            active: (tagFilters.get(id) ?? empty).has(p),
          }));
      }
    }
  };

  const tagClicked = (id: tagId, value: primitive, active: boolean, mode: 'replace' | 'toggle') => {
    let activeSet = tagFilters.get(id) ?? new SvelteSet();

    if (active) {
      activeSet.delete(value);
    } else {
      if (mode === 'replace') activeSet.clear();
      activeSet.add(value);
    }

    tagFilters.set(id, activeSet);
  };

  const tagFilter = (id: tagId): ((row: TableRow) => boolean) => {
    const active = tagFilters.get(id) ?? empty;
    if (active.size === 0) return () => true;
    const [type, key] = splitInTwo(id, ':');
    switch (type) {
      case 'parameter':
        return (row) =>
          !bottomUpTraverse([row], (r) => active.has(r.parameters[key] as any)).next().done;
      case 'tag': {
        if (key === 'any') {
          return (row) => {
            const res =
              new Set(row.tags).intersection(active).size !== 0 ||
              ('children' in row &&
                some(
                  bottomUpTraverse(row.children ?? []),
                  (c) => new Set(c.tags).intersection(active).size !== 0,
                ));
            return res;
          };
        }
        return (row) => active.has(row.tags.includes(key));
      }
    }
  };

  let tagFilters = new SvelteMap<tagId, SvelteSet<primitive>>([
    ['parameter:datacenter', new SvelteSet()],
  ]);

  $effect(() => {
    if (useAnyTagFilter) {
      untrack(() => {
        if (!tagFilters.has('tag:any')) tagFilters.set('tag:any', new SvelteSet());
      });
    } else {
      untrack(() => tagFilters.delete('tag:any'));
    }
  });

  $effect(() => {
    const filters = [...tagFilters.keys()].map(tagFilter);

    filterFn = (r) => filters.every((f) => f(r));
  });
</script>

<div class={['flex gap-4', class_]}>
  {#key rows}
    {#each [...tagFilters.keys()] as id, i (i)}
      {@const options = tagOptions(id).toSorted((a, b) => a.title.localeCompare(b.title))}
      {#if options.length > 1}
        <TagFilter
          {id}
          {options}
          onClick={({ id, value, active, mode }) => {
            tagClicked(id, value, active, mode);
          }}
          {tagLabel}
          {tagTitle}
        />
      {/if}
    {/each}
    {#each [...thresholdFilters.keys()] as id, i (i)}
      <ThresholdFilter {id} />
    {/each}
  {/key}
</div>
